// src/components/compliance/modals/QueueStatusTasksModal.jsx

import React from "react";
import {
  buildControlLabel,
  cleanValue,
  formatTimestamp,
  getSummaryChipStyle,
  getTaskStatusLabel,
  normalizeText,
  safeNumber,
} from "../../../utils/complianceFormatters";
import { isTaskOverdue } from "../../../utils/remediationTaskHelpers";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const QUEUE_CONFIG = {
  open: {
    eyebrow: "OASYS · OPEN WORK",
    title: "Open Remediation Tasks",
    subtitle: "Tasks that have not started yet or are waiting to be picked up. Open tasks may also appear in Overdue when SLA has been breached.",
    primaryMetric: "Open Tasks",
    tone: "default",
    accent: "#60a5fa",
    empty: "No open remediation tasks are currently loaded for this framework.",
  },
  ready_for_validation: {
    eyebrow: "OASYS · READY FOR VALIDATION",
    title: "Ready for Validation",
    subtitle: "Tasks marked ready for validation and waiting for the next validation pass.",
    primaryMetric: "Ready Tasks",
    tone: "accent",
    accent: "#60a5fa",
    empty: "No ready-for-validation tasks are currently loaded for this framework.",
  },
  ready: {
    eyebrow: "OASYS · READY FOR VALIDATION",
    title: "Ready for Validation",
    subtitle: "Tasks marked ready for validation and waiting for the next validation pass.",
    primaryMetric: "Ready Tasks",
    tone: "accent",
    accent: "#60a5fa",
    empty: "No ready-for-validation tasks are currently loaded for this framework.",
  },
  unassigned: {
    eyebrow: "OASYS · UNASSIGNED WORK",
    title: "Unassigned Remediation Tasks",
    subtitle: "Tasks missing an owner. Unassigned work may also appear in Overdue or Validation Failed when those risk overlays apply.",
    primaryMetric: "Unassigned Tasks",
    tone: "warn",
    accent: "#fbbf24",
    empty: "No unassigned remediation tasks are currently loaded for this framework.",
  },
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function normalizeQueueType(value) {
  const normalized = normalizeText(value || "open");
  if (normalized === "ready") return "ready_for_validation";
  return QUEUE_CONFIG[normalized] ? normalized : "open";
}

function getTaskTitle(task = {}) {
  const title = cleanValue(task.title || task.task_title, "");
  if (title) return title;
  return `Remediate ${buildControlLabel(task.control_id || task.controlId, task.rule_id || task.ruleId)}`;
}

function getOwnerLabel(task = {}) {
  return cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
}

function getPriorityLabel(task = {}) {
  const priority = task.priority ?? task.priority_score ?? task.priorityScore;
  return priority === null || priority === undefined || priority === "" ? "P—" : `P${priority}`;
}

function getDueDateValue(task = {}) {
  return task.due_date || task.dueDate || task.deadline_at || task.deadlineAt || null;
}

function getLastUpdatedValue(task = {}) {
  return task.updated_at || task.updatedAt || task.created_at || task.createdAt || task.due_date || task.dueDate || null;
}

function statusTone(task = {}) {
  const status = normalizeText(task.status || task.task_status);
  const validationStatus = normalizeText(task.validation_status || task.validationStatus);

  if (validationStatus === "failed") return "danger";
  if (isTaskOverdue(task)) return "danger";
  if (status === "ready_for_validation") return "accent";
  if (status === "in_progress") return "warn";
  if (status === "blocked") return "warn";
  return "default";
}

function validationLabel(task = {}) {
  const raw = cleanValue(task.validation_status || task.validationStatus, "pending");
  return raw.replace(/_/g, " ");
}

function buildDriftParams(task = {}, framework) {
  return {
    framework: task.framework || framework,
    controlId: task.control_id || task.controlId || undefined,
    ruleId: task.rule_id || task.ruleId || undefined,
    resourceId: task.resource_id || task.resourceId || undefined,
  };
}

function getTaskKey(task = {}, index) {
  return (
    task.id ||
    [
      task.framework,
      task.control_id || task.controlId,
      task.rule_id || task.ruleId,
      task.resource_id || task.resourceId,
      index,
    ]
      .filter(Boolean)
      .join("::")
  );
}

function sortQueueTasks(tasks = []) {
  return [...(Array.isArray(tasks) ? tasks : [])].sort((left, right) => {
    const rightOverdue = isTaskOverdue(right) ? 1 : 0;
    const leftOverdue = isTaskOverdue(left) ? 1 : 0;
    if (rightOverdue !== leftOverdue) return rightOverdue - leftOverdue;

    const rightUpdated = new Date(getLastUpdatedValue(right) || 0).getTime();
    const leftUpdated = new Date(getLastUpdatedValue(left) || 0).getTime();
    const rightTime = Number.isFinite(rightUpdated) ? rightUpdated : 0;
    const leftTime = Number.isFinite(leftUpdated) ? leftUpdated : 0;
    if (rightTime !== leftTime) return rightTime - leftTime;

    return safeNumber(left.priority, 999) - safeNumber(right.priority, 999);
  });
}

function buildTaskActions({ task, framework, onReviewTask, onFixPlan, onViewGuidance, onOpenDrift }) {
  return [
    {
      key: "review",
      label: "Review / Manage",
      onClick: () => onReviewTask(task, { returnTo: "queue_status_tasks" }),
    },
    {
      key: "fix_plan",
      label: "Fix Plan",
      onClick: () => onFixPlan(task, "task", { returnTo: "queue_status_tasks" }),
    },
    {
      key: "guidance",
      label: "Guidance",
      onClick: () => onViewGuidance(task, { returnTo: "queue_status_tasks" }),
    },
    {
      key: "drift",
      label: "Open in Drift",
      onClick: () => onOpenDrift(buildDriftParams(task, framework)),
    },
  ];
}

export default function QueueStatusTasksModal({
  open,
  framework,
  queueType = "open",
  tasks = [],
  totalCount = 0,
  loading = false,
  error = "",
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const selectedType = normalizeQueueType(queueType);
  const config = QUEUE_CONFIG[selectedType] || QUEUE_CONFIG.open;
  const rows = sortQueueTasks(tasks);
  const shownCount = rows.length;
  const total = totalCount || shownCount;
  const overdueCount = rows.filter((task) => isTaskOverdue(task)).length;
  const highPriorityCount = rows.filter((task) => safeNumber(task.priority, 999) <= 25).length;
  const unassignedCount = rows.filter((task) => getOwnerLabel(task) === "Unassigned").length;

  return (
    <ModalShell
      open={open}
      ariaLabel={config.title}
      eyebrow={config.eyebrow}
      title={config.title}
      subtitle={config.subtitle}
      onClose={onClose}
    >
      <div style={metricGridStyle}>
        <MetricTile label={config.primaryMetric} value={total} sub={`${framework} queue`} color={config.accent} />
        <MetricTile
          label="High Priority"
          value={highPriorityCount}
          sub="priority ≤ 25"
          color={highPriorityCount > 0 ? "#fbbf24" : "#34d399"}
        />
        <MetricTile
          label="Overdue"
          value={overdueCount}
          sub="SLA breached"
          color={overdueCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Unassigned"
          value={unassignedCount}
          sub="missing owner"
          color={unassignedCount > 0 ? "#fbbf24" : "#34d399"}
        />
      </div>

      {error ? (
        <div style={{ ...getSummaryChipStyle("danger"), justifySelf: "start", borderRadius: 12 }}>
          {error}
        </div>
      ) : null}

      <ModalSection
        title="Queue Detail"
        description="Use Review / Manage to update ownership or workflow state, Fix Plan for remediation, or Guidance for context."
        badge={loading ? "loading…" : `${shownCount} shown${total > shownCount ? ` of ${total}` : ""}`}
        badgeTone={config.tone}
      >
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>
            Loading selected remediation queue…
          </div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            {config.empty}
          </div>
        ) : (
          rows.map((task, index) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const resourceId = task.resource_id || task.resourceId || "";
            const dueDate = getDueDateValue(task);
            const ownerLabel = getOwnerLabel(task);
            const taskStatus = getTaskStatusLabel(task.status || task.task_status || "open");
            const tone = statusTone(task);
            const isValidationFailed = normalizeText(task.validation_status) === "failed";

            return (
              <TaskQueueCard
                key={getTaskKey(task, index)}
                title={getTaskTitle(task)}
                dueLabel={dueDate ? `Due ${formatTimestamp(dueDate)}` : "No due date"}
                chips={[
                  { label: taskStatus, tone },
                  { label: validationLabel(task), tone: isValidationFailed ? "danger" : "default" },
                  { label: ownerLabel, tone: ownerLabel === "Unassigned" ? "warn" : "default" },
                  { label: getPriorityLabel(task), tone: "default" },
                ]}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: cleanValue(resourceId, "—"), wrap: true },
                ]}
                context={
                  task.failure_reason || task.reason_summary || task.remediation_hint
                    ? cleanValue(task.failure_reason || task.reason_summary || task.remediation_hint, "Review the task for remediation details.")
                    : ""
                }
                actions={buildTaskActions({ task, framework, onReviewTask, onFixPlan, onViewGuidance, onOpenDrift })}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
